import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { AUDITION_SCENE_STATUSES } from "@/lib/crm";

const empty = {
  name: "", description: "", text: "", file_name: "",
  status: "to_prepare",
};

export default function SceneDialog({ open, onOpenChange, auditionId, scene, onSaved }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const isEdit = !!scene;
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(scene ? { ...empty, ...scene } : empty);
  }, [open, scene]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setReading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/pdf/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const extracted = r.data?.text || "";
      set("text", extracted);
      set("file_name", f.name);
      toast.success(t("new.pdf_ok"));
    } catch { toast.error(t("new.pdf_fail")); }
    finally { setReading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error(t("aud.form.required"));
      return;
    }
    setSaving(true);
    try {
      let r;
      if (isEdit) {
        r = await api.patch(`/crm/auditions/${auditionId}/scenes/${scene.scene_id}`, form);
      } else {
        r = await api.post(`/crm/auditions/${auditionId}/scenes`, form);
      }
      toast.success(t("asc.saved"));
      onOpenChange?.(false);
      onSaved?.(r.data);
    } catch { toast.error(t("asc.save_fail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="scene-dialog"
        className="max-w-2xl bg-stone-950 border-stone-800 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-stone-100">
            {isEdit ? t("crm.action.edit") : t("asc.add")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="label-cap">{t("asc.name")} *</label>
            <input
              data-testid="asc-form-name"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="script-input w-full"
            />
          </div>

          <div>
            <label className="label-cap">{t("asc.description")}</label>
            <textarea
              data-testid="asc-form-description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder={t("asc.description_ph")}
              rows={2}
              className="script-input w-full resize-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label-cap">{t("asc.text")}</label>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="asc-form-upload"
                className="ghost-btn !py-1 !px-2 text-xs"
              >
                {reading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                {reading ? t("new.reading") : t("new.upload_pdf")}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={onFile}
                className="hidden"
              />
            </div>
            <div className="mono text-[10px] text-stone-500 -mt-1 mb-1 text-right">{t("new.upload_formats")}</div>
            <textarea
              data-testid="asc-form-text"
              value={form.text}
              onChange={(e) => set("text", e.target.value)}
              placeholder={t("asc.text_ph")}
              rows={8}
              className="script-input w-full resize-none"
            />
            {form.file_name && (
              <div className="mt-1 text-[11px] text-stone-500 flex items-center gap-1">
                <FileText size={11} /> {t("asc.file_uploaded")} {form.file_name}
              </div>
            )}
          </div>

          <div>
            <label className="label-cap">{t("asc.status")}</label>
            <select
              data-testid="asc-form-status"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
              className="script-input w-full"
            >
              {AUDITION_SCENE_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`asc.status.${s}`)}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange?.(false)} className="ghost-btn">
              {t("action.cancel")}
            </button>
            <button
              type="submit"
              data-testid="asc-form-save"
              disabled={saving}
              className="pill-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {t("crm.form.save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
